"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="232000" className="flex items-center gap-2">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex items-center gap-0">
        {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex items-center">
            {i > 0 && <Separator orientation="vertical" className="h-4 mx-2 opacity-20" />}
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="text-foreground text-sm font-default font-medium px-3 py-2 rounded-md hover:text-primary transition-all duration-300 tracking-wide">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </div>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 px-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted/40 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-4">
      {menuItems.map((item, i) => <SheetClose key={item.id} data-index={i} asChild>
          <Link to={item.href || "#"} className="text-foreground font-default font-medium text-base px-4 py-3 rounded-lg hover:bg-accent transition-all duration-200 block">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b ${scrolled ? "bg-background/80 backdrop-blur-lg border-border/40 shadow-md" : "bg-background border-transparent"}`}>
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        {}
        <Link to="/" className="flex items-center">
          <span className="font-default font-bold text-2xl md:text-3xl leading-none tracking-tight" style={{
          color: "var(--color-logo)"
        }}>
            Eirion Technology
          </span>
        </Link>

        {}
        <div className="hidden md:flex items-center gap-4">
          <MenuProvider menu_id="40">
            <DesktopMenuConsumer />
          </MenuProvider>

          <Separator orientation="vertical" className="h-5 opacity-20 mx-1" />

          <Button asChild className="rounded-sm px-6 py-2 text-sm font-default font-semibold border-0 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-primary/20 animate-pulse" style={{
          background: "linear-gradient(135deg, hsl(190 90% 50%), hsl(270 70% 60%))",
          color: "var(--primary-foreground)"
        }}>
            <Link to="#">Get Started</Link>
          </Button>
        </div>

        {}
        <div className="flex md:hidden items-center">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Menu className="size-6 text-foreground" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 sm:max-w-xs pt-10 bg-background border-l border-border/40">
              <SheetHeader className="pb-4">
                <SheetTitle className="font-default font-bold text-2xl text-left" style={{
                color: "var(--color-logo)"
              }}>
                  Eirion Technology
                </SheetTitle>
              </SheetHeader>

              <Separator className="mb-4 opacity-20" />

              <MenuProvider menu_id="40">
                <MobileMenuConsumer />
              </MenuProvider>

              <div className="px-4 mt-6">
                <SheetClose asChild>
                  <Button asChild className="w-full rounded-sm text-sm font-default font-semibold border-0 shadow-lg" style={{
                  background: "linear-gradient(135deg, hsl(190 90% 50%), hsl(270 70% 60%))",
                  color: "var(--primary-foreground)"
                }}>
                    <Link to="#">Get Started</Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}